import { useEffect, useRef } from 'react';
import {
  Box, Flex, Icon, IconButton, Link,
  useToast,
  List,
  ListItem,
  Heading,
  Tooltip,
  Text,
  useDimensions,
  Badge,
} from '@chakra-ui/react';
import { selectedDatabaseIdState, useIsUserInRole } from '@transport-insights/uikit';
import {
  FiArrowLeft, FiArrowRight, FiGrid, FiExternalLink,
  FiInfo,
  FiLink2,
} from 'react-icons/fi';
import { Link as RouterLink, Redirect, useLocation } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import MoreInfoDrawer from '@src/components/shared/MoreInfoDrawer';
import { useAllowedReportsList } from '../hooks/useReportsList';
import { selectedTemporaryRegionRoadTypeState } from '../state';
import RegionRoadTypeSelector from './RegionRoadTypeSelector';
import ReportListModal from './ReportListModal';

export default function Pagination({
  breadcrumbs, pdfPrint = null, children, moreInfo = null,
}) {
  const hasDeveloperRole = useIsUserInRole('Developer');
  const ref = useRef();
  const dimension = useDimensions(ref, true);
  const toast = useToast();
  const setTemporaryRegionRoadTypeState = useSetRecoilState(selectedTemporaryRegionRoadTypeState);
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const reportsList = useAllowedReportsList(rcaId);
  const { pathname } = useLocation();
  const currentReportUrl = pathname.split('/').pop();

  // Filter reports list to only enabled reports and reports with a url
  // Check if devModeReport is true and verify hasDeveloperRole, otherwise show the report
  const enabledReportsList = Object.values(reportsList).filter((x) => x.enabled
      && x.url
      && (x.devModeReport ? hasDeveloperRole : true));
  const currentReport = Object.values(reportsList).find((x) => x.url === currentReportUrl && x.enabled && x.url && (x.devModeReport ? hasDeveloperRole : true));

  const prevIndex = enabledReportsList.findIndex((report) => report.url === currentReportUrl) - 1;
  const nextIndex = enabledReportsList.findIndex((report) => report.url === currentReportUrl) + 1;

  const nextReport = nextIndex !== enabledReportsList.length ? enabledReportsList[nextIndex] : enabledReportsList[0];
  const prevReport = prevIndex !== -1 ? enabledReportsList[prevIndex] : enabledReportsList[enabledReportsList.length - 1];

  const isScrolling = dimension?.borderBox.top === 80;

  // Check if the current report has a regionRoadType and set it to the temporary state
  useEffect(
    () => {
      if (currentReport?.regionRoadType) {
        setTemporaryRegionRoadTypeState(currentReport.regionRoadType);
      } else {
        setTemporaryRegionRoadTypeState(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentReport],
  );

  // If currentReport is undefined it means it is not available for the selected RCA
  // Redirect to the dashboard with a message
  if (!currentReport) {
    toast({
      title: 'Report Unavailable',
      description: 'Sorry the report is not available for the selected RCA',
      status: 'info',
      duration: 4000,
      isClosable: true,
    });
    return <Redirect to="/performance/dashboard" />;
  }

  return (
    <Box
      position="sticky"
      top="80px"
      pb={2}
      px={4}
      mb={2}
      mx="-16px"
      background="gray.50"
      zIndex="250"
      ref={ref}
      boxShadow={isScrolling ? '1px 7px 10px -9px rgba(0,0,0,0.15);' : 'none'}
    >
      <Flex align="center" justify="space-between">
        <Flex align="center">
          <Tooltip label="Back to Dashboard">
            <IconButton
              as={RouterLink}
              to="/performance/dashboard"
              borderRadius="full"
              colorScheme="blue"
              variant="outline"
              icon={<FiGrid />}
            />
          </Tooltip>
          <RegionRoadTypeSelector ml={4} />
          {breadcrumbs && breadcrumbs}
        </Flex>
        <Flex align="center" justify="space-between">
          {prevReport
            && (
            <Tooltip label={prevReport.name}>
              <Link
                as={RouterLink}
                to={`/performance/dashboard/${prevReport.url}`}
                color="brand.orange.500"
                display="flex"
                align="center"
                borderRadius="sm"
                p={2}
                _hover={{
                  textDecoration: 'none',
                  color: 'white',
                  background: 'brand.orange.500',
                }}
              >
                <Icon as={FiArrowLeft} boxSize={8} />
              </Link>
            </Tooltip>
            )}
          {nextReport
            && (
            <Tooltip label={nextReport.name}>
              <Link
                as={RouterLink}
                to={`/performance/dashboard/${nextReport.url}`}
                color="brand.orange.500"
                display="flex"
                align="center"
                borderRadius="sm"
                p={2}
                _hover={{
                  textDecoration: 'none',
                  color: 'white',
                  background: 'brand.orange.500',
                }}
              >
                <Icon as={FiArrowRight} boxSize={8} />
              </Link>
            </Tooltip>
            )}
          <ReportListModal />
          {pdfPrint}
          <MoreInfoDrawer title="More Information" size="xl">
            {moreInfo}
            {currentReport?.relevantLinks?.length > 0 && (
            <>
              <Heading as="h3" size="sm" mb={2} mt={4}>
                Relevant Links
              </Heading>
              <List styleType="disc" stylePosition="inside">
                {currentReport.relevantLinks.map((link) => {
                  const isExternal = /^https?:\/\//i.test(link.url);

                  return (
                    <ListItem key={link.url}>
                      <Link href={link.url} fontSize="sm" py={1} isExternal>
                        {link.title}
                        {isExternal && (
                          <Icon as={FiExternalLink} boxSize={3} ml={3} />
                        )}
                      </Link>
                    </ListItem>
                  );
                })}
              </List>
            </>
            )}
            <Box mt={4} mb={2} border="1px solid" borderColor="gray.100" p={3} borderRadius="md">
              <Badge colorScheme="gray" mb={2}>Tips & Tricks</Badge>
              <Text fontSize="sm">
                • Click the
                {' '}
                <Icon as={FiInfo} boxSize={4} />
                {' '}
                icon in the page for more detailed descriptions and data sources
              </Text>
              <Text fontSize="sm">
                • Hover over the
                {' '}
                <Icon as={FiLink2} boxSize={4} />
                {' '}
                icon for the related ONF page
              </Text>
            </Box>
          </MoreInfoDrawer>
        </Flex>
      </Flex>
      {children}
    </Box>
  );
}
